import { defineStore } from 'pinia';
import { mollieService } from '@/services/mollieService';

/**
 * Mollie / Slim Innen integration store.
 *
 * Holds the connection status (KYC + onboarding) needed by the integration
 * settings screen. The summary used everywhere else in the SPA (Btn-gating
 * on the invoice detail / public payment page) lives on
 * `invoiceStore.triggerAvailability.integrations.mollie` — this store is
 * purely for the deeper "Beheer Mollie" surfaces.
 */
export const useMollieStore = defineStore('mollie', {
  state: () => ({
    status: null,
    statusLoaded: false,
    statusLoading: false,
    error: null,
    disconnecting: false,
  }),

  getters: {
    isConnected: (state) => !!state.status?.connected,
    needsReauth: (state) => state.status?.reason === 'MOLLIE_REAUTH_REQUIRED',
    onboardingComplete: (state) => state.status?.onboarding?.status === 'completed',
    canAcceptLivePayments: (state) => !!state.status?.can_accept_live_payments,
    isTestMode: (state) => state.status?.mode === 'test',
    profileName: (state) => state.status?.profile_name || null,
  },

  actions: {
    /**
     * Fetch the latest Mollie connection status. Called on the integration
     * detail screen and re-fetched after a successful connect/disconnect.
     *
     * @param {{ force?: boolean }} [options]
     */
    async fetchStatus({ force = false } = {}) {
      if (this.statusLoaded && !force) return this.status;
      this.statusLoading = true;
      this.error = null;
      try {
        const data = await mollieService.getStatus();
        this.status = data;
        this.statusLoaded = true;
        return data;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.statusLoading = false;
      }
    },

    /**
     * Open the Mollie OAuth flow in a popup window and resolve once the
     * Funnelflow callback page posts the result back via `postMessage`.
     *
     * Falls back to `window.location.assign` if the popup was blocked, so
     * the user still gets a chance to connect.
     *
     * @returns {Promise<{ success: boolean, error?: string }>}
     */
    async startOAuth() {
      const { url } = await mollieService.getAuthorizeUrl();
      if (!url) {
        throw new Error('No OAuth URL returned');
      }

      const popup = window.open(
        url,
        'mollie-connect',
        'width=540,height=720,resizable=yes,scrollbars=yes'
      );

      if (!popup) {
        // Popup blocked — fall back to a full-page redirect so the user
        // can still complete the flow. The backend redirects back to the
        // SPA after a successful connect.
        window.location.assign(url);
        return { success: false, error: 'popup_blocked' };
      }

      return new Promise((resolve) => {
        let settled = false;

        const cleanup = () => {
          window.removeEventListener('message', onMessage);
          clearInterval(closedTimer);
        };

        const finish = (payload) => {
          if (settled) return;
          settled = true;
          cleanup();
          try {
            popup.close();
          } catch {
            /* popup may already be closed */
          }
          resolve(payload);
        };

        const onMessage = (event) => {
          const data = event?.data;
          if (!data || typeof data !== 'object') return;
          if (data.type !== 'mollie:oauth') return;
          finish({ success: !!data.success, error: data.error });
        };

        window.addEventListener('message', onMessage);

        // Detect manual close — Mollie's popup-close happens via JS, but
        // if the user closes it themselves we still want to stop waiting.
        const closedTimer = setInterval(() => {
          if (popup.closed) {
            finish({ success: false, error: 'popup_closed' });
          }
        }, 500);
      });
    },

    async disconnect() {
      this.disconnecting = true;
      try {
        await mollieService.disconnect();
        this.status = { connected: false, reason: 'MOLLIE_NOT_CONNECTED' };
        this.statusLoaded = true;
        return true;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.disconnecting = false;
      }
    },

    invalidate() {
      this.statusLoaded = false;
    },

    $reset() {
      this.status = null;
      this.statusLoaded = false;
      this.statusLoading = false;
      this.error = null;
      this.disconnecting = false;
    },
  },
});

export default useMollieStore;
